import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/layout";
import Header from "../../components/header";
import SubHeader from "../../components/SubHeader";
import SiteButtons from "../../components/SiteButtons";
import Container from "../../components/Container";
import BodyGroupHeading from "../../components/BodyGroupHeading";
import ReusableTable from "../../components/ReusableTable";
import Pagination from "../../components/pagination";
import { URLS } from "../../_config";
import SiteModal from "../../components/SiteModal";
import Icon from "../../components/icons";
import { useDialog } from "../../store/api/hooks/dialogHook";
import { useCustomersApi } from "../../store/api/hooks/customersApiHook";
import toast from "react-hot-toast";
import { useMenuPermissionsSelector } from "../../store/selectors/userSelector";
import Permission from "../../_config/Permission";
import FormattedDate from "../../components/elements/FormattedDate";
import { useTranslation } from "react-i18next";
import { getLookupDisplayValue, getCustomerGroupDisplayName, useLookup } from "../../_common/hookFunction";

interface PageProps {}

const CustomerGroups: React.FC<PageProps> = ({}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("customers");
  const { confirm } = useDialog();
  const customersApi = useCustomersApi();
  const lookup = useLookup();
  const getGroupTypeDisplay = (rawGroupType: any) => {
    const groupTypeItem = lookup.getCustomerGroupType()?.find((g: any) => g.lookupValue === rawGroupType);
    return groupTypeItem ? getLookupDisplayValue(groupTypeItem, t) : rawGroupType;
  };
  const menuPermissions = useMenuPermissionsSelector();
  
  const permissions = useMemo(() => {
    return menuPermissions[URLS.CUSTOMER_GROUPS] || [];
  }, [menuPermissions]);

  const [showCustomerGroupModal, setShowCustomerGroupModal] =
    useState<boolean>(false);
  const [customerGroupsData, setCustomerGroupsData] = useState<any>(null);
  const [allCustomerGroupsData, setAllCustomerGroupsData] = useState<any>(null); // Frontend pagination - Store all fetched groups
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1); // Frontend pagination
  const [pageSize, setPageSize] = useState<number>(10); // Frontend pagination

  const closeAddCustomerGroupModal = (success: boolean = false) => {
    setShowCustomerGroupModal(false);
    if (success) {
      fetchCustomerGroups();
    }
  };

  const editCustomerGroup = (item: any) => {
    const groupType = item?.groupType == "Manual Group" ? "MTQ3" : "custom";
    navigate(URLS.EDIT_CUSTOMER_GROUP(groupType.toLowerCase(), item?.customerGroupId || item.id));
  };

  const viewCustomerGroup = (item: any) => {
    navigate(`${URLS.CUSTOMERS}?group=${item?.customerGroupId || ""}`);
  };

  const confirmDeleteCustomerGroup = (item: any) => {
    // console.log("Delete customer group:", item);
    customersApi.deleteCustomerGroup(
      { customerGroupId: item.customerGroupId || item.id },
      (response: any) => {
        if (!response?.success) {
          toast.error(
            response?.errorMessage || t("customerGroups.toasts.deleteFailed")
          );
          return;
        }
        toast.success(t("customerGroups.toasts.deleteSuccess", { name: item.name }));
        fetchCustomerGroups(); // Refresh the list after deletion
      },
      (error: any) => {
        console.log("error =>", error);
        toast.error(t("customerGroups.toasts.deleteFailed"));
      }
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const fetchCustomerGroups = () => {
    setLoading(true);
    setCustomerGroupsData(null);
    setAllCustomerGroupsData(null); // Frontend pagination
    customersApi.listCustomerGroups(
      {},
      (response: any) => {
        if (!response?.success) {
          // setCustomerGroupsData([]); // Frontend pagination - Commented out
          setAllCustomerGroupsData([]); // Frontend pagination
          setTotalRows(0);
          setLoading(false);
          return;
        }
        const mappedGroups = (response.data || []).map(
          (group: any, index: number) => ({
            ...group,
            id: group.customerGroupId || `group-${index}`,
            name: group.customerGroupName,
            groupType: group.groupType,
            dateCreated: group.createDate,
            lastModified: group.modifyDate,
            numberOfCustomers: group.customerCount || 0,
            level: 0,
            hasChildren: false,
            expanded: false,
            parentId: undefined,
            isDragging: false,
            isDragOver: false,
          })
        );
        // setCustomerGroupsData(mappedGroups); // Frontend pagination - Commented out
        setAllCustomerGroupsData(mappedGroups); // Frontend pagination - Store all data
        setTotalRows(mappedGroups.length || 0); // Frontend pagination - Use length
        setLoading(false);
      },
      (error: any) => {
        // setCustomerGroupsData([]); // Frontend pagination - Commented out
        setAllCustomerGroupsData([]); // Frontend pagination
        setTotalRows(0);
        setLoading(false);
        toast.error(t("customerGroups.toasts.fetchFailed"));
      }
    );
  };

  // Frontend pagination - Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Frontend pagination - Handle page size change
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Frontend pagination - Slice data based on current page and page size
  useEffect(() => {
    if (allCustomerGroupsData && allCustomerGroupsData.length > 0) {
      const maxPage = Math.ceil(allCustomerGroupsData.length / pageSize);
      if (currentPage > maxPage) {
        setCurrentPage(maxPage);
        return;
      }
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = allCustomerGroupsData.slice(startIndex, endIndex);
      setCustomerGroupsData(paginatedData);
    } else if (allCustomerGroupsData && allCustomerGroupsData.length === 0) {
      setCustomerGroupsData([]);
    }
  }, [allCustomerGroupsData, currentPage, pageSize]);

  useEffect(() => {
    fetchCustomerGroups();
  }, []);

  return (
    <Layout>
      <Header />
      <Permission value={permissions} check="R" page>
      <div className="flex-grow overflow-auto">
        <SubHeader
          title={t("customerGroups.page.title")}
          subTitle={t("customerGroups.page.subtitle")}
        >
          <Permission value={permissions} check="W">
          <SiteButtons
            data={[
              {
                text: t("customerGroups.buttons.addGroup"),
                click: () => {
                  setShowCustomerGroupModal(true);
                },
              },
            ]}
          />
          </Permission>
        </SubHeader>
        <Container className="mb-12">
          <BodyGroupHeading
            subtitle={loading ? "" : t("customerGroups.resultsFound", { count: totalRows })}
            showHr={false}
            className="flex items-center justify-between"
          />

          <ReusableTable
            key={`customer-groups-table-${customerGroupsData?.length || 0}`}
            data={customerGroupsData}
            fixedHeight="calc(100vh - 200px)"
            columns={[
              {
                key: "name",
                label: t("customerGroups.columns.name"),
                sortable: false,
                width: "250px",
                render: (item: any) =>
                  getCustomerGroupDisplayName(item, t) || item.name || item.groupName || "-",
              },
              {
                key: "groupType",
                label: t("customerGroups.columns.groupType"),
                width: "150px",
                sortable: false,
                render: (item: any) => (
                  <span>{getGroupTypeDisplay(item.groupType || item.type) || t("customerGroups.values.custom")}</span>
                ),
              },
              {
                key: "dateCreated",
                label: t("customerGroups.columns.dateCreated"),
                width: "180px",
                sortable: false,
                render: (item: any) => {
                  const dateValue =
                    item.dateCreated || item.createdDate || item.createDate;
                  return dateValue ? <FormattedDate date={dateValue} format="dateTime" /> : "-";
                },
              },
              {
                key: "lastModified",
                label: t("customerGroups.columns.lastModified"),
                width: "180px",
                sortable: false,
                render: (item: any) => {
                  const dateValue =
                    item.lastModified || item.modifiedDate || item.updateDate || item.createdDate || item.dateCreated;
                  return dateValue ? <FormattedDate date={dateValue} format="dateTime" /> : "-";
                },
              },
              {
                key: "numberOfCustomers",
                label: t("customerGroups.columns.numberOfCustomers"),
                width: "180px",
                align: "center",
                sortable: false,
                render: (item: any) => (
                  <span className="font-medium flex justify-center">
                    {(
                      item.numberOfCustomers ||
                      item.customerCount ||
                      0
                    ).toLocaleString()}
                  </span>
                ),
              },
            ]}
            enableExpand={false}
            enableDrag={false}
            isRearranging={false}
            getRowActions={(item: any) => (
              <>
                <button
                  className="p-1 text-secondary hover:bg-cyan-50 rounded"
                  onClick={() => {
                    const url = `${URLS.CUSTOMERS}?group=${item?.customerGroupId || ""}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  title={t("customerGroups.tooltips.viewGroup")}
                >
                  <Icon name="eye" className="w-4 h-4" title={t("customerGroups.values.view")}/>
                </button>
                  <Permission value={permissions} check="W">
                  <button
                    className={`p-1 text-secondary hover:bg-cyan-50 rounded ${item.isDefault ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => editCustomerGroup(item)}
                    title={t("customerGroups.tooltips.editGroup")}
                    disabled={item.isDefault}
                  >
                    <Icon name="edit" className="w-4 h-4" title={t("details.actions.edit")}/>
                  </button>
                  </Permission>
                  <Permission value={permissions} check="D">
                  <button
                    className={`p-1 text-red-500 hover:bg-red-50 rounded ${item.isDefault ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() =>
                      confirm({
                        type: "delete",
                        title: t("customerGroups.deleteConfirm.title"),
                        message: t("customerGroups.deleteConfirm.message", {
                          name: item.groupName || item.name,
                        }),
                        onConfirm: () =>
                          confirmDeleteCustomerGroup({ ...item, name: item.groupName || item.name }),
                      })
                    }
                    title={t("customerGroups.tooltips.deleteGroup")}
                    disabled={item.isDefault}
                  >
                    <Icon name="delete" className="w-4 h-4" title={t("details.actions.delete")}/>
                  </button>
                  </Permission>
              </>
            )}
          />

          {/* Frontend pagination - Pagination component */}
          {totalRows > 0 && (
            <Pagination
              currentPage={currentPage}
              totalRecords={totalRows}
              perPage={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </Container>
        {showCustomerGroupModal && (
          <SiteModal
            isOpen={showCustomerGroupModal}
            onClose={closeAddCustomerGroupModal}
            title={t("customerGroups.modal.title")}
          >
            <div className="p-3">
              {[
                {
                  label: t("customerGroups.values.custom"),
                  description: t("customerGroups.modal.customDescription"),
                  value: `custom`,
                  disable: false,
                },
                {
                  label: t("customerGroups.modal.manualLabel"),
                  description: t("customerGroups.modal.manualDescription"),
                  value: `MTQ3`,
                  disable: false,
                },
              ].map((type: any) => (
                <button
                  key={type.value}
                  disabled={type.disable}
                  className={`w-full px-2 ${
                    !type.disable ? "hover:bg-gray-200" : " opacity-70"
                  } overflow-hidden rounded-md flex items-center justify-between py-3 border-b last:border-0`}
                  onClick={() => {
                    navigate(
                      URLS.CREATE_CUSTOMER_GROUP(type.value.toLowerCase())
                    );
                  }}
                >
                  <p className={`grow text-left`}>
                    <span className={`block text-black`}>{type.label}</span>
                    <span className="block text-sm text-gray-500 text-left">
                      {type.description}
                    </span>
                  </p>
                  <Icon name="arrowright" />
                </button>
              ))}
            </div>
          </SiteModal>
        )}
      </div>
      </Permission>
    </Layout>
  );
};

export default CustomerGroups;
